import type {
  Campaign,
  CampaignReference,
  CampaignReferences,
  CampaignScope,
  CampaignWithReference,
  YammerState,
} from '../../state/types';
import { findById as findCampaignCoverImage } from '../campaignCoverImage/selectors';
import { findCampaignById } from '../campaigns/selectors';
import { createFindById, createFindByIdThrows } from '../findById';

export const findCampaignReferenceById = createFindById('campaignReferences');
export const findCampaignReferenceByIdThrows = createFindByIdThrows('campaignReferences');

type GetCampaignTelemetryId = (
  state: YammerState,
  campaignId: Campaign['id']
) => CampaignReference['telemetryId'] | undefined;
export const getCampaignTelemetryId: GetCampaignTelemetryId = (state, campaignId) =>
  findCampaignReferenceById(state, campaignId)?.telemetryId;

type GetCampaignReferencesState = (state: YammerState) => CampaignReferences;
export const getCampaignReferencesState: GetCampaignReferencesState = (state) => state.campaignReferences;

type GetFulfilledCampaignReferences = (state: YammerState) => Record<string, CampaignReference>;
export const getFulfilledCampaignReferences: GetFulfilledCampaignReferences = (state) =>
  state.campaignReferences.fulfilled.byId;

type GetCampaignWithReference = (state: YammerState, campaignId: Campaign['id']) => CampaignWithReference | undefined;
export const getCampaignWithReference: GetCampaignWithReference = (state, campaignId) => {
  const campaignReference = findCampaignReferenceById(state, campaignId);
  const campaign = findCampaignById(state, campaignId);
  const campaignCoverImage = findCampaignCoverImage(state, campaignId);

  if (!campaignReference || !campaign || !campaignCoverImage) {
    return undefined;
  }

  const campaignWithReference = {
    ...campaign,
    ...campaignReference,
    ...campaignCoverImage,
  };

  return campaignWithReference;
};

type IsMTOCampaign = (state: YammerState, campaignId: Campaign['id'] | undefined) => boolean;
export const isMTOCampaign: IsMTOCampaign = (state, campaignId) => {
  if (campaignId === undefined) {
    return false;
  }

  return getCampaignScope(state, campaignId)?.type === 'MULTI_TENANT_ORGANIZATION';
};

type IsGroupCampaign = (state: YammerState, campaignId: Campaign['id'] | undefined) => boolean;
export const isGroupCampaign: IsGroupCampaign = (state, campaignId) => {
  if (campaignId === undefined) {
    return false;
  }

  return getCampaignScope(state, campaignId)?.type === 'GROUP';
};

type GetGroupIdForCampaign = (state: YammerState, campaignId: Campaign['id']) => string | null;
export const getGroupIdForCampaign: GetGroupIdForCampaign = (state, campaignId) => {
  const scope = getCampaignScope(state, campaignId);

  return scope?.type === 'GROUP' ? scope.groupId : null;
};

type GetGroupIdForCampaignThrows = (state: YammerState, campaignId: Campaign['id']) => string;
export const getGroupIdForCampaignThrows: GetGroupIdForCampaignThrows = (state, campaignId) => {
  const scope = getCampaignScope(state, campaignId);

  if (scope?.type !== 'GROUP') {
    throw new Error(`Campaign ${campaignId} is not a group campaign.`);
  }

  return scope.groupId;
};

type GetCampaignScope = (state: YammerState, campaignId: Campaign['id']) => CampaignScope | undefined;
export const getCampaignScope: GetCampaignScope = (state, campaignId) =>
  findCampaignReferenceById(state, campaignId)?.scope;
